import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider as MuiDivider, Grid, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { find } from 'lodash';

import {
  LeaderboardConfigFragment,
  QueryGetLeaderboardForUserArgs,
  useGetUserLeaderboardQuery,
} from 'api';
import { useNotify } from 'hooks';
import { Leaderboard } from 'Links';

import Header from './Header';
import UserScore from './UserScore';
import LeaderboardTable from './Table';
import Loader from './Loader';

interface IProps {
  configs: LeaderboardConfigFragment[];
  variables: QueryGetLeaderboardForUserArgs;
}

const WIDGET_LIMIT = 3;

const useStyles = makeStyles(theme => ({
  root: {
    padding: `0 ${theme.spacing(2)}px 0`,
  },
  button: {
    padding: `${theme.spacing(1)}px 0`,
  },
}));

const Widget: FC<IProps> = ({ configs, variables }) => {
  const classes = useStyles();
  const notify = useNotify();
  const [selectedConfig, setSelectedConfig] = useState(configs[0]);
  const { t } = useTranslation();

  const queryVariables = {
    leaderboard_config_id: selectedConfig.id,
    limit: WIDGET_LIMIT,
    ...variables,
  };

  const { data, isLoading } = useGetUserLeaderboardQuery(queryVariables, {
    onError: notify.queryError,
  });

  function handleChange(configId: string) {
    const nextConfig = find(configs, ['id', configId]);
    setSelectedConfig(nextConfig);
  }

  function renderBody() {
    if (isLoading) {
      return <Loader />;
    }

    const { leaderboard_scores, user_score } = data.getLeaderboardForUser;
    const { items } = leaderboard_scores;

    return (
      <Fragment>
        {user_score && <UserScore userScore={user_score} />}
        {user_score && <MuiDivider />}
        <LeaderboardTable items={items} />
      </Fragment>
    );
  }

  if (!selectedConfig) {
    return null;
  }

  return (
    <Grid item xs>
      <Paper className={classes.root}>
        <Header configs={configs} selectedConfig={selectedConfig} onChange={handleChange} />
        <MuiDivider />
        {renderBody()}
        <MuiDivider />
        <Button component={Leaderboard} fullWidth className={classes.button}>
          {t('components:seeLeaderboards')}
        </Button>
      </Paper>
    </Grid>
  );
};

export default Widget;
